import { Box, Button, TextField, Typography } from "@mui/material";
import { usdaNameToCommonName, usdaNameToUnit } from "@notemeal/nutrient-utils";
import { ALL_FORM_NUTRIENTS, FORM_NUTRIENTS_WITHOUT_MACROS } from "shared-ui/NutrientAmount/customNutrientUtils";
import { inputToNumber } from "shared-ui/utils/inputUtils";
import { round } from "shared-ui/utils/numbers";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { getServingUnits } from "../../Serving/utils";
import { BaseFoodFormType } from "../FoodFormSchema/FoodFormSchema";
import { FoodServingType } from "../FoodFormSchema/FoodServingSchema";
import { MACRO_KEY_TO_PER_100G_FIELD, changeNutrientAmounts } from "../FoodFormSchema/foodServingsUtils";
import { ViewAllNutrientsDialog } from "./ViewAllNutrientsDialog";

const getDefaultServingLabel = (serving: FoodServingType) => {
  const { defaultAmount, units, weight } = serving;
  const { unit, customUnits, unitPrefix, unitSuffix } = units;
  const servingUnits =
    !unit && !customUnits
      ? "serving(s)"
      : getServingUnits({
          unit,
          customUnits,
          unitPrefix,
          unitSuffix,
        });
  return `${defaultAmount || ""} ${servingUnits}, ${(weight || 0) * (defaultAmount || 1)} g`;
};

interface FoodServingNutrientsProps {
  defaultServing?: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

export const FoodServingNutrients = ({ defaultServing, form, disabled }: FoodServingNutrientsProps) => {
  const [viewAllOpen, setViewAllOpen] = useState(false);

  return (
    <Box
      sx={{
        border: "1px solid #D0D5DD",
        borderRadius: "6px",
        p: 2,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Default Serving Nutrients</Typography>
          {defaultServing && (
            <Typography variant="body2" color="textSecondary">
              {getDefaultServingLabel(defaultServing)}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          onClick={() => setViewAllOpen(true)}
          sx={{
            height: 28,
            fontSize: 12,
            fontWeight: 600,
            borderColor: "#D0D5DD",
            color: "text.primary",
            borderRadius: "6px",
            textTransform: "none",
            boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.1)",
            px: 1,
            minWidth: "auto",
            flexShrink: 0,
          }}
        >
          View All
        </Button>
      </Box>
      {defaultServing && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {FORM_NUTRIENTS_WITHOUT_MACROS.map(nutrient => (
            <NutrientRow
              key={nutrient}
              disabled={disabled}
              nutrient={nutrient}
              serving={defaultServing}
              form={form} />
          ))}
        </Box>
      )}
      <ViewAllNutrientsDialog
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        form={form}
        disabled={disabled}
      />
    </Box>
  );
};

interface NutrientRowProps {
  nutrient: (typeof ALL_FORM_NUTRIENTS)[number];
  serving: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

const NutrientRow = ({ nutrient, serving, form, disabled }: NutrientRowProps) => {
  const { watch, setValue, getValues } = form;
  const [watchedUsdaFdcId] = watch(["usdaFdcId"]);
  const { nutrientAmounts } = serving;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: "20px" }}>{`${usdaNameToCommonName(nutrient)} (${usdaNameToUnit(
        nutrient
      )})`}</Typography>
      <TextField
        variant="outlined"
        size="small"
        value={nutrientAmounts && nutrientAmounts[nutrient] ? round(inputToNumber(nutrientAmounts[nutrient] || "0") || 0) : ""}
        type="number"
        placeholder="0"
        disabled={disabled || watchedUsdaFdcId !== null}
        onChange={e => {
          const { newNutrientAmountsManualEntry, newServings, macroUpdate } = changeNutrientAmounts({
            foodForm: getValues(),
            nutrientName: nutrient,
            nutrientValueInput: e.target.value,
          });
          setValue("nutrientAmountsManualEntry", newNutrientAmountsManualEntry);
          if (macroUpdate) {
            setValue(MACRO_KEY_TO_PER_100G_FIELD[macroUpdate.macro], macroUpdate.macroValuePer100g);
          }
          newServings.forEach((s, index) => {
            setValue(`servings.${index}.nutrientAmounts`, s.nutrientAmounts);
            if (macroUpdate) {
              setValue(`servings.${index}.${macroUpdate.macro}`, s[macroUpdate.macro], { shouldValidate: true });
            }
          });
        }}
        sx={{
          "&.MuiTextField-root": { marginTop: 0 },
          width: 80,
          flexShrink: 0,
          "& .MuiOutlinedInput-root": {
            height: 28,
            borderRadius: "6px",
            paddingLeft: 0,
            paddingRight: 0,
            "& input": {
              padding: "0 12px",
              fontSize: 12,
              lineHeight: "16px",
            },
          },
        }}
        inputProps={{ min: 0 }}
      />
    </Box>
  );
};
